import { useEffect, useRef } from 'react';
import { getDoc, runTransaction, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { teamDoc, teamSharedCollectionDoc, userDoc } from '../../../firebase/collections';
import { useAuth } from '../../../hooks/useAuth';
import { useCollectionSummary } from '../../../hooks/useCollectionSummary';
import {
  COLLECTION_SUMMARY_SET_OPTIONS,
  collectionSummaryToFirestore,
  isTeamProjectionCurrent,
  summarizeCollection,
} from '../../../utils/collectionSummary';
import { deriveAdminUids, deriveMemberUids } from '../types';
import {
  effectiveShareCollection,
  loadUserCollectionMap,
  syncTeamSharedCollection,
  visibilitySharesWithTeam,
} from '../utils/teamSharedCollectionSync';

function uidListsMatch(expected: string[], actual: unknown): boolean {
  if (!Array.isArray(actual)) return false;
  if (actual.length !== expected.length) return false;
  const set = new Set(actual);
  return expected.every((id) => set.has(id));
}

/**
 * When the user is on a team with public/team collection visibility, keep
 * members[].shareCollection, denormalized uid lists, and the sharedCollections
 * projection in sync. Covers create-team / join flows that never wrote a
 * projection, and heals stale shareCollection=false while visibility is already
 * public.
 *
 * Also applies join-request consent: admins cannot bump another user's
 * collectionVisibility, so a private member with shareCollection=true is
 * upgraded to `team` on their next session.
 *
 * Full-collection `getDocs` runs only when the projection revision does not
 * match `users/{uid}.collectionSummary.revision` (or the summary is missing).
 */
export function useTeamCollectionHeal() {
  const { user } = useAuth();
  const collectionSummary = useCollectionSummary();
  const lastKey = useRef<string>('');
  const shareBumpKey = useRef<string>('');

  useEffect(() => {
    if (!user?.teamId || !user.uid) return;

    const teamId = user.teamId;
    const uid = user.uid;
    const visibility = user.collectionVisibility;
    const displayName = user.displayName;
    const slug = user.slug;
    const summary = collectionSummary;

    void (async () => {
      try {
        // Join-request accept only sets users.teamId; apply share consent here.
        if (visibility === 'private') {
          const bumpKey = `${uid}:${teamId}:share-bump`;
          if (shareBumpKey.current === bumpKey) return;
          const teamSnap = await getDoc(teamDoc(teamId));
          if (!teamSnap.exists()) return;
          const members = teamSnap.data()?.members as
            | Array<{ uid: string; shareCollection?: boolean }>
            | undefined;
          const me = members?.find((m) => m.uid === uid);
          if (me?.shareCollection !== true) {
            shareBumpKey.current = bumpKey;
            return;
          }
          await setDoc(
            userDoc(uid),
            { collectionVisibility: 'team', updatedAt: Timestamp.now() },
            { merge: true }
          );
          shareBumpKey.current = bumpKey;
          // Re-run via auth user snapshot once visibility flips to team.
          return;
        }

        if (!visibilitySharesWithTeam(visibility)) return;

        const key = `${uid}:${teamId}:${visibility}`;
        if (lastKey.current === key) return;

        await runTransaction(db, async (tx) => {
          const tRef = teamDoc(teamId);
          const teamSnap = await tx.get(tRef);
          if (!teamSnap.exists()) return;
          const teamData = teamSnap.data() as {
            members: Array<Record<string, unknown> & { uid: string; shareCollection: boolean }>;
            memberUids?: string[];
            adminUids?: string[];
          };
          const me = teamData.members.find((m) => m.uid === uid);
          if (!me) return;

          const share = effectiveShareCollection(visibility, me.shareCollection === true);
          const nextMembers = teamData.members.map((m) =>
            m.uid === uid ? { ...m, shareCollection: share } : m
          );
          const memberUids = deriveMemberUids(nextMembers as never);
          const adminUids = deriveAdminUids(nextMembers as never);
          const needsMemberPatch = me.shareCollection !== share;
          const needsUidLists =
            !uidListsMatch(memberUids, teamData.memberUids) ||
            !uidListsMatch(adminUids, teamData.adminUids);

          // Always rewrite when share flag or denormalized uid lists are stale.
          // Skipping here leaves sharedCollections writes permission-denied when
          // memberUids is missing even though members[] already includes the user.
          if (!needsMemberPatch && !needsUidLists) return;

          tx.set(
            tRef,
            {
              members: nextMembers,
              memberUids,
              adminUids,
              updatedAt: Timestamp.now(),
            },
            { merge: true }
          );
        });

        const projSnap = await getDoc(teamSharedCollectionDoc(teamId, uid));
        const projection = projSnap.exists()
          ? (projSnap.data() as { revision?: unknown })
          : null;

        if (isTeamProjectionCurrent(projection, summary)) {
          lastKey.current = key;
          return;
        }

        if (summary && summary.uniqueCards === 0 && summary.totalCards === 0) {
          await syncTeamSharedCollection({
            teamId,
            uid,
            displayName,
            slug,
            entries: {},
            visibility,
            shareCollection: true,
            revision: summary.revision,
          });
          lastKey.current = key;
          return;
        }

        const entries = await loadUserCollectionMap(uid);
        if (!summary) {
          const next = summarizeCollection(entries, 1);
          await setDoc(
            userDoc(uid),
            { collectionSummary: collectionSummaryToFirestore(next) },
            COLLECTION_SUMMARY_SET_OPTIONS,
          );
          await syncTeamSharedCollection({
            teamId,
            uid,
            displayName,
            slug,
            entries,
            visibility,
            shareCollection: true,
            revision: next.revision,
          });
        } else {
          await syncTeamSharedCollection({
            teamId,
            uid,
            displayName,
            slug,
            entries,
            visibility,
            shareCollection: true,
            revision: summary.revision,
          });
        }
        lastKey.current = key;
      } catch (err) {
        console.warn('[useTeamCollectionHeal] failed', err);
      }
    })();
  }, [
    user?.uid,
    user?.teamId,
    user?.collectionVisibility,
    user?.displayName,
    user?.slug,
    collectionSummary,
  ]);
}
